import { Fragment, useState, type ReactNode } from "react";
import { api } from "../api";
import { caseRef, missingFields, pathRef, shortAct, shortLabel } from "../format";
import type { Citation, DraftRef, Message, Rating, Verdict } from "../types";
import { IconChevron, IconDoc } from "./Icons";
import { StepList } from "./StepList";

const EMPTY: Rating = {
  law_correct: null,
  version_correct: null,
  answers_question: null,
  comment: "",
};

/**
 * Three axes rather than a thumbs up/down, because they are three different
 * failure modes with three different fixes: the wrong act is a retrieval
 * problem, the wrong version a currency problem, and a correct citation that
 * does not answer the question a generation problem.
 */
const AXES: { key: "law_correct" | "version_correct" | "answers_question"; label: string }[] = [
  { key: "law_correct", label: "Norma citată e corectă" },
  { key: "version_correct", label: "Versiunea e cea în vigoare" },
  { key: "answers_question", label: "Răspunde la întrebare" },
];

/** Where a source points: "art. 86 alin. (1)", or the decision. */
function where(c: Citation): string {
  return c.kind === "decision" ? caseRef(c) : pathRef(c.path) ?? shortLabel(c.label);
}

type Block =
  | { kind: "h"; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "table"; head: string[]; rows: string[][] };

/** "| a | b |" -> ["a", "b"]. */
function cells(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

/**
 * The answer's layout: "## " headings, "- " and "1. " lists, pipe tables,
 * paragraphs. That is all the prompt allows the model to use, and all this
 * reads; a full Markdown renderer would be a dependency for five features
 * and an invitation to render whatever else it emits. Tables come from the
 * backend's creeaza_tabel, not from the model's hand, but any pipe table
 * renders the same. Answers written before the layout existed are plain
 * paragraphs, and render as they did.
 */
function blocks(content: string): Block[] {
  const out: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) out.push({ kind: "p", text: para.join("\n") });
    para = [];
  };
  for (const raw of content.split("\n")) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flush();
      continue;
    }
    // A table row. The first row of a table is its head; the "| --- |"
    // line under it is syntax, not a row.
    if (/^\s*\|.*\|\s*$/.test(line)) {
      flush();
      const last = out[out.length - 1];
      if (/^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/.test(line)) continue;
      if (last && last.kind === "table") last.rows.push(cells(line));
      else out.push({ kind: "table", head: cells(line), rows: [] });
      continue;
    }
    const heading = /^\s{0,3}#{1,4}\s+(.+)$/.exec(line);
    if (heading) {
      flush();
      out.push({ kind: "h", text: heading[1].replace(/\s+#+$/, "") });
      continue;
    }
    // "* " only with a space after it, so "**bold**" opening a line is text.
    const bullet = /^\s*[-*•]\s+(.+)$/.exec(line);
    const numbered = /^\s*\d{1,2}[.)]\s+(.+)$/.exec(line);
    const item = bullet ?? numbered;
    if (item) {
      flush();
      const kind: "ul" | "ol" = bullet ? "ul" : "ol";
      const last = out[out.length - 1];
      if (last && (last.kind === "ul" || last.kind === "ol") && last.kind === kind) {
        last.items.push(item[1]);
      } else {
        out.push({ kind, items: [item[1]] });
      }
      continue;
    }
    // An indented line under a list item continues that item.
    const last = out[out.length - 1];
    if (!para.length && /^\s+/.test(raw) && last && (last.kind === "ul" || last.kind === "ol")) {
      last.items[last.items.length - 1] += " " + line.trim();
      continue;
    }
    para.push(line);
  }
  flush();
  return out;
}

/**
 * **bold** and the [n] markers, inside a heading, paragraph or list item.
 */
function inline(
  text: string,
  citations: Citation[],
  active: string | null,
  onCite: (c: Citation) => void,
): ReactNode[] {
  const out: ReactNode[] = [];
  const parts = text.split(/(\*\*[^*]+\*\*|\[\d{1,3}\])/g);
  parts.forEach((part, i) => {
    if (!part) return;
    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    if (bold) {
      out.push(<strong key={i}>{bold[1]}</strong>);
      return;
    }
    const mark = /^\[(\d{1,3})\]$/.exec(part);
    if (mark) {
      const c = citations[Number(mark[1]) - 1];
      if (!c) return; // a marker with no source is dropped, never left dead
      out.push(
        <button
          key={i}
          className={c.chunk_id === active ? "cite on" : "cite"}
          onClick={() => onCite(c)}
          aria-label={`Sursa ${mark[1]}: ${where(c)}`}
        >
          {mark[1]}
        </button>,
      );
      return;
    }
    out.push(<Fragment key={i}>{part}</Fragment>);
  });
  return out;
}

/** The answer as plain text, sources listed, for Copiază and Exportă. */
export function plainAnswer(message: Message): string {
  const body = message.content
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^\s{0,3}#{1,4}\s+/gm, "");
  const cites = message.citations ?? [];
  if (!cites.length) return body;
  const sources = cites
    .map((c, i) =>
      c.kind === "decision"
        ? `[${i + 1}] ${c.title}, ${caseRef(c)} (practică judiciară` +
          (c.part === "facts" ? "; fragmentul redă instanțele inferioare" : "") +
          ")" + (c.url ? ` ${c.url}` : "")
        : `[${i + 1}] ${shortAct(c.title)}, ${where(c)}` +
          (c.is_current ? "" : " (versiune anterioară)"))
    .join("\n");
  return `${body}\n\nSurse:\n${sources}`;
}

export function AnswerCard({
  message,
  activeSource,
  activeDraft,
  onOpenDocument,
  onOpenDraft,
}: {
  message: Message;
  /** chunk_id of the citation the panel is showing, to mark it. */
  activeSource: string | null;
  activeDraft: string | null;
  onOpenDocument: (c: Citation) => void;
  onOpenDraft: (id: string) => void;
}) {
  const [stepsOpen, setStepsOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [rating, setRating] = useState<Rating>(message.rating ?? EMPTY);
  const [comment, setComment] = useState(message.rating?.comment ?? "");
  const [copied, setCopied] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);

  if (message.error) {
    return (
      <div className="a a-error" role="alert">
        <p>{message.content}</p>
      </div>
    );
  }

  const citations = message.citations ?? [];
  const steps = message.steps ?? [];
  const drafts = message.drafts ?? [];
  // Toggling a source that is already open closes the panel, which the
  // parent does by comparing; here every click is just "this one".
  const open = (c: Citation) => onOpenDocument(c);

  async function save(next: Rating) {
    setRating(next);
    setRateError(null);
    try {
      await api.rate(message.id, next);
    } catch (e) {
      setRateError(e instanceof Error ? e.message : "Evaluarea nu a fost salvată.");
    }
  }

  function axis(key: (typeof AXES)[number]["key"], v: boolean) {
    // Clicking the chosen answer again clears it, so a misclick is
    // recoverable without a third "unsure" button.
    const current = rating[key] as Verdict;
    void save({ ...rating, [key]: current === v ? null : v });
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(plainAnswer(message));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard refused (permissions, insecure origin): nothing to do */
    }
  }

  const layout = blocks(message.content);
  const say = (t: string) => inline(t, citations, activeSource, open);

  return (
    <div className="a">
      {/* Where the live list was, folded: the steps stay where the lawyer
          watched them happen instead of vanishing when the answer lands. */}
      {steps.length > 0 && (
        <div className="steps-fold">
          <button
            className="steps-toggle"
            aria-expanded={stepsOpen}
            onClick={() => setStepsOpen((o) => !o)}
          >
            <IconChevron size={14} />
            Cum am verificat · {steps.length === 1 ? "1 pas" : `${steps.length} pași`}
          </button>
          {stepsOpen && <StepList steps={steps} />}
        </div>
      )}

      {layout.map((b, i) => {
        if (b.kind === "h") return <h3 className="a-h" key={i}>{say(b.text)}</h3>;
        if (b.kind === "p") return <p className="a-text" key={i}>{say(b.text)}</p>;
        if (b.kind === "table") {
          return (
            // Wrapped so a wide table scrolls on a phone instead of
            // pushing the whole answer sideways.
            <div className="a-table-wrap" key={i}>
              <table className="a-table">
                <thead>
                  <tr>{b.head.map((h, j) => <th key={j}>{say(h)}</th>)}</tr>
                </thead>
                <tbody>
                  {b.rows.map((r, j) => (
                    <tr key={j}>
                      {b.head.map((_, k) => <td key={k}>{say(r[k] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        const items = b.items.map((t, j) => <li key={j}>{say(t)}</li>);
        return b.kind === "ul"
          ? <ul className="a-list" key={i}>{items}</ul>
          : <ol className="a-list" key={i}>{items}</ol>;
      })}

      {(citations.length > 0 || drafts.length > 0) && (
        <div className="sources">
          {drafts.map((d) => (
            <DraftChip key={d.id} draft={d} on={d.id === activeDraft} onOpen={onOpenDraft} />
          ))}
          {citations.map((c, i) => (
            <button
              key={c.chunk_id}
              className={c.chunk_id === activeSource ? "srcchip on" : "srcchip"}
              onClick={() => open(c)}
            >
              <span className="n">{i + 1}</span>
              <span>
                <span className="t">
                  {c.kind === "decision" ? caseRef(c) : `${shortAct(c.title)} · ${where(c)}`}
                </span>
                {c.kind === "decision" ? (
                  // Not "în vigoare": a judgment guides, it does not bind,
                  // and the chip must not read like the law.
                  <span className="s case">practică judiciară</span>
                ) : c.is_current ? (
                  <span className="s">în vigoare</span>
                ) : (
                  // Presenting repealed text as current is the worst thing
                  // this product can do, so it is on the chip itself.
                  <span className="s old">versiune anterioară</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="row">
        <button
          className={rateOpen ? "ghost on" : "ghost"}
          aria-expanded={rateOpen}
          onClick={() => setRateOpen((o) => !o)}
        >
          Evaluați răspunsul
        </button>
        <button className="ghost" onClick={() => void copy()}>
          {copied ? "Copiat" : "Copiază"}
        </button>
      </div>

      {rateOpen && (
        <div className="rate">
          {AXES.map((a) => (
            <div className="axis" key={a.key}>
              <span id={`ax-${message.id}-${a.key}`}>{a.label}</span>
              <div className="yn" role="group" aria-labelledby={`ax-${message.id}-${a.key}`}>
                {[true, false].map((v) => (
                  <button
                    key={String(v)}
                    className={rating[a.key] === v ? "on" : undefined}
                    aria-pressed={rating[a.key] === v}
                    onClick={() => axis(a.key, v)}
                  >
                    {v ? "Da" : "Nu"}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <label className="sr-only" htmlFor={`cm-${message.id}`}>Comentariu</label>
          <textarea
            id={`cm-${message.id}`}
            rows={2}
            placeholder="Ce ar trebui corectat? (opțional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onBlur={() => comment !== rating.comment && void save({ ...rating, comment })}
          />
          {rateError && <p className="error" role="alert">{rateError}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * A document the answer produced, as a chip beside the sources. The count of
 * empty required fields is on it because it is the difference between a
 * document ready to check and a starting point.
 */
function DraftChip({
  draft,
  on,
  onOpen,
}: {
  draft: DraftRef;
  on: boolean;
  onOpen: (id: string) => void;
}) {
  return (
    <button className={on ? "srcchip on" : "srcchip"} onClick={() => onOpen(draft.id)}>
      <span className="n"><IconDoc size={16} /></span>
      <span>
        <span className="t">{draft.title}</span>
        <span className={draft.missing_count > 0 ? "s warn" : "s"}>
          {draft.missing_count > 0 ? missingFields(draft.missing_count) : "complet"}
        </span>
      </span>
    </button>
  );
}
